import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ClipboardList, Printer } from 'lucide-react';

import GenericSelect from '../../components/ui/GenericSelect';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import AcknowledgementBanner from './components/AcknowledgementBanner';
import ObjectionCard from './components/ObjectionCard';
import TrendCard from './components/TrendCard';
import FinalLevelCard from './components/FinalLevelCard';
import ResultMeetingCard from './components/ResultMeetingCard';
import {
  useResultsPeriods, useMyPeriodResultsApi, useMyResultsHistory, useMyObjection,
} from './config';

// PRD PM §5.3 (Task 29) — çalışan dönem sonu sekmeli sonuç ekranı ("Dönem
// Sonuçlarım"). Task 25/26/27/28'in sayfa desenini izler: dönem seçici
// (TeamGoalsPage'deki periodOptions/GenericSelect birebir), koşullu sekmeler
// (Tabs bileşeni) — hangi sekmenin gösterileceği TAMAMEN backend'in
// goalsTab/feedbackTab null'lığına bağlı (bkz. config/index.js yorumu; Task
// 14'ün "hangi sistem aktifse o sekme dolar" kuralı). Erken erişim/abonelik/
// bilinmeyen dönem — hepsi aynı "sonuçlar hazır değil" boş-durumuna düşer
// (config hook'u skipErrorToast ile interceptor toast'ını zaten bastırıyor).
const Container = styled.div``;

// Bu sayfa (self-servis, Tabs, DataTable YOK) MyPreboardingPage'in Title+Tabs
// desenini izler — PageHeader'ın eski flex/space-between'i tek çocukla hiçbir
// şey yapmıyordu (ölü CSS), düz bir blok yeterli.
const PageHeader = styled.div`
  margin-bottom: 18px;
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.metrics.font.size.xl};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const PeriodSelectWrap = styled.div`
  min-width: 280px;
  margin-bottom: 16px;
`;

// P1-C — karne bağlantısı (baskı-optimizasyonlu sayfa; karar 4).
const ReportCardRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  overflow: hidden;
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 24px;
  text-align: center;
`;

const EmptyIconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const EmptyText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  max-width: 420px;
`;

const GoalCard = styled.div`
  background: #fff;
  border: 1px solid #e8ecf0;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 16px;
`;

const GoalCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const GoalTypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: #eff6ff;
  color: #0291ef;
`;

const GoalTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GoalDescription = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 12px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 4px;

  th, td {
    text-align: left;
    padding: 8px 10px;
    font-size: ${({ theme }) => theme.metrics.font.size.sm};
    border-bottom: 1px solid #f1f5f9;
  }

  th {
    color: ${({ theme }) => theme.colors.text.dark.little};
    font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  }

  td {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 8px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const KpiField = styled.div``;

const KpiLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 4px;
`;

const KpiValue = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 4px;
  overflow: hidden;
  margin: 4px 0 6px;
`;

const ProgressBarInner = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.primary.main};
  width: ${(p) => p.$pct}%;
  transition: width 0.3s ease;
`;

const SelfAssessmentBox = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.bgColor};
  border-radius: 6px;
`;

const SelfAssessmentLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 4px;
`;

const SelfAssessmentText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const WeightedOverallCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eff6ff;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 18px 22px;
  margin-bottom: 20px;
`;

const WeightedOverallLabel = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const WeightedOverallValue = styled.div`
  font-size: 28px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.primary.main};
`;

// Faz E (spec 2026-08-08 §4, E5) — "Yetkinlik Kırılımı" kartı, çalışan
// varyantı (EmployeeFeedbackSummaryDto.competencyBreakdown — task-3-report.md
// §1: targetLevel bu DTO'da TİP DÜZEYİNDE yok, hedef-kıyas mantığı bu yüzden
// bu dosyada hiç YAZILMADI, İK/yönetici varyantı için ayrı bir kart
// PerformanceReportCardPage.js'te). Görsel dil bu sayfanın kendi
// ProgressBarOuter/Inner'ıyla AYNI (goal completionPct barı).
const CompetencyCard = styled(Card)`
  padding: 20px;
  margin-top: 16px;
`;

const CompetencyCardTitle = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 14px;
`;

const CompetencyRow = styled.div`
  margin-bottom: 16px;
  &:last-child { margin-bottom: 0; }
`;

const CompetencyRowHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 6px;
`;

const CompetencyName = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CompetencyQuestionCount = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  white-space: nowrap;
`;

const CompetencyAverageText = styled.div`
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-top: 4px;
`;

function clampPct(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return 0;
  return Math.min(100, Math.max(0, n));
}

function GoalsTabContent({ goals, t }) {
  if (!goals || goals.length === 0) {
    return <Card><Centered><EmptyText>{t('performance.goal.noGoals')}</EmptyText></Centered></Card>;
  }

  return (
    <>
      {goals.map((goal) => (
        <GoalCard key={goal.id}>
          <GoalCardHeader>
            <GoalTypeBadge>{t(`performance.goal.goalType.${goal.goalType}`, { defaultValue: goal.goalType })}</GoalTypeBadge>
            <GoalTitle>{goal.title}</GoalTitle>
          </GoalCardHeader>
          {goal.description && <GoalDescription>{goal.description}</GoalDescription>}

          {goal.goalType === 'okr' ? (
            <StyledTable>
              <thead>
                <tr>
                  <th>{t('performance.goal.keyResultTitle')}</th>
                  <th>{t('performance.goal.employeeScore')}</th>
                  <th>{t('performance.goal.managerScore')}</th>
                  <th>{t('performance.goal.managerComment')}</th>
                </tr>
              </thead>
              <tbody>
                {(goal.keyResults || []).map((kr) => (
                  <tr key={kr.id}>
                    <td>{kr.title}</td>
                    <td>{kr.employeeScore ?? '-'}</td>
                    <td>{kr.managerScore ?? '-'}</td>
                    <td>{kr.managerComment || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          ) : (
            <KpiGrid>
              <KpiField>
                <KpiLabel>{t('performance.goal.kpiIndicatorName')}</KpiLabel>
                <KpiValue>{goal.kpiIndicatorName || '-'}</KpiValue>
              </KpiField>
              <KpiField>
                <KpiLabel>{t('performance.goal.kpiTargetValue')}</KpiLabel>
                <KpiValue>{goal.kpiTargetValue ?? '-'}</KpiValue>
              </KpiField>
              <KpiField>
                <KpiLabel>{t('performance.goal.kpiActualValue')}</KpiLabel>
                <KpiValue>{goal.kpiActualValue ?? '-'}</KpiValue>
              </KpiField>
              <KpiField style={{ gridColumn: '1 / -1' }}>
                <KpiLabel>{t('performance.goal.kpiCompletionPct')}</KpiLabel>
                <ProgressBarOuter>
                  <ProgressBarInner $pct={clampPct(goal.completionPct)} />
                </ProgressBarOuter>
                <KpiValue>{goal.completionPct != null ? `${goal.completionPct}%` : '-'}</KpiValue>
              </KpiField>
            </KpiGrid>
          )}

          {goal.selfAssessmentText && (
            <SelfAssessmentBox>
              <SelfAssessmentLabel>{t('performance.goal.selfAssessment')}</SelfAssessmentLabel>
              <SelfAssessmentText>{goal.selfAssessmentText}</SelfAssessmentText>
            </SelfAssessmentBox>
          )}
        </GoalCard>
      ))}
    </>
  );
}

// Faz E (spec §4, E5) — çalışan varyantı: DTO'da targetLevel hiç yok, bu
// yüzden burada hedef-kıyas dalı YOK (FE koşul üretmez — brief). Boş kırılım
// (etiketli soru yok) → kart hiç görünmez (`breakdown.length === 0` → null).
function CompetencyBreakdownCard({ breakdown, t }) {
  if (!breakdown || breakdown.length === 0) return null;

  return (
    <CompetencyCard data-role="competency-breakdown-card">
      <CompetencyCardTitle>{t('competencyBreakdown.cardTitle')}</CompetencyCardTitle>
      {breakdown.map((item) => (
        <CompetencyRow key={item.competencyId} data-role="competency-breakdown-row">
          <CompetencyRowHeader>
            <CompetencyName>{item.name}</CompetencyName>
            <CompetencyQuestionCount>
              {t('competencyBreakdown.questionCount', { count: item.questionCount })}
            </CompetencyQuestionCount>
          </CompetencyRowHeader>
          {item.average != null ? (
            <>
              <ProgressBarOuter>
                <ProgressBarInner $pct={clampPct((Number(item.average) / 5) * 100)} />
              </ProgressBarOuter>
              <CompetencyAverageText>{item.average} / 5</CompetencyAverageText>
            </>
          ) : (
            <CompetencyAverageText>-</CompetencyAverageText>
          )}
        </CompetencyRow>
      ))}
    </CompetencyCard>
  );
}

function FeedbackTabContent({ summary, t }) {
  const perQuestion = summary?.perQuestion || [];

  return (
    <>
      <Card style={{ padding: 20 }}>
        <WeightedOverallCard>
          <WeightedOverallLabel>{t('performance.results.weightedOverall')}</WeightedOverallLabel>
          <WeightedOverallValue>
            {summary?.weightedOverall != null ? summary.weightedOverall : '-'}
          </WeightedOverallValue>
        </WeightedOverallCard>

        {perQuestion.length === 0 ? (
          <EmptyText>{t('performance.results.noQuestions')}</EmptyText>
        ) : (
          <StyledTable>
            <thead>
              <tr>
                <th>{t('performance.results.question')}</th>
                <th>{t('performance.results.average')}</th>
              </tr>
            </thead>
            <tbody>
              {perQuestion.map((q) => (
                <tr key={q.questionId}>
                  <td>{q.text}</td>
                  <td>{q.average != null ? q.average : '-'}</td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        )}
      </Card>

      <CompetencyBreakdownCard breakdown={summary?.competencyBreakdown} t={t} />
    </>
  );
}

// PM Analitik Faz 2 (E18 "Performansım" > Sonuçlarım sekmesi) — gömülü kullanımda çalışma alanının dönemiyle açılır
// (`initialPeriodId`, listede varsa; ör. Özet'teki "İtiraz Et"). Rota olarak açıldığında verilmez; davranış aynen eskisidir.
export default function MyPeriodResultsPage({ initialPeriodId = null } = {}) {
  const { t } = useTranslation();
  const history = useHistory();

  const { periods, periodsFetching } = useResultsPeriods();
  const [selectedPeriodId, setSelectedPeriodId] = useState(null);

  useEffect(() => {
    if (!selectedPeriodId && periods.length > 0) {
      setSelectedPeriodId(initialPeriodId && periods.some((p) => p.id === initialPeriodId) ? initialPeriodId : periods[0].id);
    }
  }, [periods, selectedPeriodId, initialPeriodId]);

  const periodOptions = useMemo(() => periods.map((p) => ({
    id: p.id,
    name: `${p.name} (${t(`performance.period.status.${p.status}`, { defaultValue: p.status })})`,
  })), [periods, t]);

  const {
    goalsTab, feedbackTab, acknowledgement, objectionWindowEndsAt, finalLevelLabel,
    notReady, fetching, acknowledge,
  } = useMyPeriodResultsApi(selectedPeriodId);

  // P1-C — "Gelişim Grafiğim" + sonuç itirazı (B paketi sabit kontratı).
  const { history: resultsHistory } = useMyResultsHistory();
  const { objection, submitObjection } = useMyObjection(selectedPeriodId);

  const availableTabs = useMemo(() => {
    const list = [];
    if (goalsTab) list.push({ key: 'goals', label: t('performance.results.tabs.goals') });
    if (feedbackTab) list.push({ key: 'feedback', label: t('performance.results.tabs.feedback') });
    return list;
  }, [goalsTab, feedbackTab, t]);

  const [activeTab, setActiveTab] = useState(null);
  useEffect(() => {
    if (availableTabs.length === 0) {
      setActiveTab(null);
      return;
    }
    if (!availableTabs.some((tb) => tb.key === activeTab)) {
      setActiveTab(availableTabs[0].key);
    }
  }, [availableTabs, activeTab]);

  // Erken erişim (409 performance.results.notReady) VE savunma amaçlı — çağrı
  // "başarılı" dönüp de her iki sekme de null gelirse (Task 14'ün gate'i bunu
  // pratikte imkansız kılıyor ama FE yine de kırılmadan aynı boş-duruma düşer).
  const showEmptyState = !!selectedPeriodId && !fetching && (notReady || availableTabs.length === 0);

  return (
    <Container>
      <PageHeader>
        <PageTitle>{t('performance.results.title')}</PageTitle>
      </PageHeader>

      <PeriodSelectWrap>
        <GenericSelect
          label={t('performance.goal.selectPeriod')}
          value={selectedPeriodId}
          onChange={setSelectedPeriodId}
          options={periodOptions}
          idKey="id"
          labelKey="name"
        />
      </PeriodSelectWrap>

      {!periodsFetching && periods.length === 0 && (
        <Card><Centered><EmptyText>{t('performance.results.noPeriods')}</EmptyText></Centered></Card>
      )}

      {periods.length > 0 && !selectedPeriodId && (
        <Card><Centered><EmptyText>{t('performance.goal.selectPeriodFirst')}</EmptyText></Centered></Card>
      )}

      {selectedPeriodId && fetching && (
        <Card><Centered><EmptyText>{t('common.loading')}</EmptyText></Centered></Card>
      )}

      {showEmptyState && (
        <Card>
          <Centered>
            <EmptyIconWrap><ClipboardList size={26} /></EmptyIconWrap>
            <EmptyText>{t('responseErrors.performance.results.notReady')}</EmptyText>
          </Centered>
        </Card>
      )}

      {selectedPeriodId && !fetching && !showEmptyState && (
        <>
          {/* P1-C — tebliğ banner'ı + okudum onayı (sonuçlar görünürken). */}
          <AcknowledgementBanner acknowledgement={acknowledgement} onAcknowledge={acknowledge} />

          {/* Plan 5 Task 2 — "Nihai Seviyem" kartı; finalLevelLabel yoksa (tebliğ
              kapalı/dönem henüz "closed" değil/seviye atanmamış) DTO-driven olarak
              hiç render edilmez (bkz. FinalLevelCard). */}
          <FinalLevelCard finalLevelLabel={finalLevelLabel} />

          {/* P1-C — sonuç itirazı (pencere durumu BE'nin objectionWindowEndsAt alanından). */}
          <ObjectionCard
            objectionWindowEndsAt={objectionWindowEndsAt}
            objection={objection}
            onSubmit={submitObjection}
          />

          {/* PM analitik P7b (E19) — sonuç görüşmesi teyidi + süreç deneyimi anketi (yayımlanmamış sonuçta kart görünmez). */}
          <ResultMeetingCard periodId={selectedPeriodId} />

          <ReportCardRow>
            <Button
              variant="outline"
              onClick={() => history.push(`/performance/report-card/${selectedPeriodId}`)}
            >
              <Printer size={15} style={{ marginRight: 6, verticalAlign: 'text-bottom' }} />
              {t('performance.results.openReportCard')}
            </Button>
          </ReportCardRow>

          <Tabs tabs={availableTabs} activeTab={activeTab} onChange={setActiveTab} />
          {activeTab === 'goals' && <GoalsTabContent goals={goalsTab} t={t} />}
          {activeTab === 'feedback' && <FeedbackTabContent summary={feedbackTab} t={t} />}
        </>
      )}

      {/* P1-C — kapalı dönem trend'i; en az bir veri noktası varsa görünür. */}
      <TrendCard history={resultsHistory} />
    </Container>
  );
}
